"use client";

import { api } from "~/trpc/react";
import { CustomCalendar } from "../ui/custom-calendar";
import {
  addDays,
  addWeeks,
  format,
  parse,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  isToday,
  isTomorrow,
  isMatch,
  isValid,
  startOfDay,
} from "date-fns";
import { srLatn } from "date-fns/locale";
import { toZonedTime } from "date-fns-tz";
import { useState, useMemo, useEffect, useRef } from "react";
import { AlertCircle, CalendarSearch } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import {
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { type RouterOutput } from "~/server/api/root";
import {
  TherapyRelationshipType,
  TherapySessionPurpose,
} from "~/prisma/browser";
import { useBookingCart } from "~/hooks/use-booking-cart";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { getTimeSlotsUrl } from "~/lib/time-slots-url";
import { TimeGrid } from "../available-time-slots/time-grid";
import {
  MobileDateStrip,
  MobileDateStripSkeleton,
} from "../available-time-slots/mobile-date-strip";
import { DrawerSheet } from "../ui/drawer-sheet";

type SuggestedAvailableDay = {
  availabilityDate: Date;
  availabilityKey: string;
  displayDayKey: string;
  displaySlot: Date;
};

function TimeGridSkeleton() {
  return (
    <div className="space-y-3">
      {[6, 6].map((count, groupIndex) => (
        <div key={groupIndex}>
          <Skeleton className="mb-1.5 h-3 w-12" />
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: count }).map((_, index) => (
              <Skeleton key={index} className="h-[58px] rounded-lg" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function getNextAvailableDays({
  selectedDate,
  freeTimeSlots,
  timezone,
}: {
  selectedDate: Date;
  freeTimeSlots: Record<string, Date[]>;
  timezone: string;
}) {
  const selectedDayKey = format(
    toZonedTime(selectedDate, timezone),
    "yyyy-MM-dd",
  );
  const seenDays = new Set<string>();

  return Object.entries(freeTimeSlots)
    .flatMap(([availabilityKey, slots]) =>
      slots.map((slot) => ({ availabilityKey, slot })),
    )
    .sort((a, b) => a.slot.getTime() - b.slot.getTime())
    .reduce<SuggestedAvailableDay[]>(
      (suggestions, { availabilityKey, slot }) => {
        const dayKey = format(toZonedTime(slot, timezone), "yyyy-MM-dd");
        if (dayKey < selectedDayKey) return suggestions;
        if (seenDays.has(dayKey)) return suggestions;

        seenDays.add(dayKey);
        suggestions.push({
          availabilityDate: parse(availabilityKey, "yyyy-MM-dd", new Date()),
          availabilityKey,
          displayDayKey: dayKey,
          displaySlot: slot,
        });
        return suggestions;
      },
      [],
    )
    .slice(0, 3);
}

function setTimeOnDate(date: Date, time: string) {
  const [hoursText, minutesText] = time.split(":");

  return setMilliseconds(
    setSeconds(
      setMinutes(
        setHours(date, Number(hoursText ?? 0)),
        Number(minutesText ?? 0),
      ),
      0,
    ),
    0,
  );
}

function parseInitialDate(value: string | null) {
  if (!value || !isMatch(value, "yyyy-MM-dd")) return null;

  const parsedDate = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsedDate) ? startOfDay(parsedDate) : null;
}

function OtherTherapistsTimeDrawer({
  initialDate,
  sessionType,
  timezone,
  isTimezoneResolved,
}: {
  initialDate: Date;
  sessionType: TherapyRelationshipType;
  timezone: string;
  isTimezoneResolved: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialDate,
  );
  const wasOpenRef = useRef(open);
  const router = useRouter();

  const [startDate, endDate] = useMemo(() => {
    const today = startOfDay(new Date());
    return [today.toISOString(), addWeeks(today, 4).toISOString()];
  }, []);

  const dailyAvailability =
    api.therapistRouter.getDailyAvailabilitySummary.useQuery(
      {
        startDate,
        endDate,
        timezone,
        sessionType,
      },
      { enabled: open && isTimezoneResolved },
    );

  const hourlyAvailability = api.therapistRouter.getHourlyAvailability.useQuery(
    {
      date: (selectedDate ?? new Date()).toISOString(),
      timezone,
      sessionType,
    },
    { enabled: open && !!selectedDate && isTimezoneResolved },
  );

  useEffect(() => {
    if (!wasOpenRef.current && open) {
      setSelectedDate(initialDate);
    }

    wasOpenRef.current = open;
  }, [initialDate, open]);

  const handleTimeSelect = (time: string) => {
    if (!selectedDate) return;

    setOpen(false);
    router.push(
      getTimeSlotsUrl(setTimeOnDate(selectedDate, time), sessionType),
    );
  };

  return (
    <DrawerSheet
      open={open}
      onOpenChange={setOpen}
      title="Izaberi drugi termin"
      description="Pogledaj kada su slobodni drugi terapeuti."
      drawerContentClassName="max-h-[85vh]"
      sheetContentClassName="w-full overflow-y-auto sm:max-w-md"
      trigger={
        <Button variant="link" className="px-0">
          Treba ti drugi termin?
        </Button>
      }
    >
      <div
        data-vaul-no-drag
        className="space-y-4 overflow-y-auto px-4 pb-6 md:px-0 md:pt-4"
      >
        {dailyAvailability.isError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <div className="flex items-center gap-2 font-medium">
              <AlertCircle className="h-4 w-4" />
              Ne možemo da učitamo slobodne datume.
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-auto px-0 text-destructive hover:bg-transparent"
              onClick={() => void dailyAvailability.refetch()}
            >
              Pokušaj ponovo
            </Button>
          </div>
        ) : dailyAvailability.isLoading ? (
          <>
            <MobileDateStripSkeleton />
            <TimeGridSkeleton />
          </>
        ) : (
          <MobileDateStrip
            selectedDate={selectedDate}
            onSelect={setSelectedDate}
            dailySummary={dailyAvailability.data}
            startDate={startDate}
          />
        )}

        {selectedDate &&
          (hourlyAvailability.isError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle className="h-4 w-4" />
                Ne možemo da učitamo slobodna vremena.
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 h-auto px-0 text-destructive hover:bg-transparent"
                onClick={() => void hourlyAvailability.refetch()}
              >
                Pokušaj ponovo
              </Button>
            </div>
          ) : hourlyAvailability.isLoading ? (
            <TimeGridSkeleton />
          ) : (
            <TimeGrid
              selectedTime={null}
              onTimeSelect={handleTimeSelect}
              hourlyAvailability={hourlyAvailability.data}
            />
          ))}
      </div>
    </DrawerSheet>
  );
}

function formatSelectedDateRelative(date: Date) {
  if (isToday(date)) return "danas";
  if (isTomorrow(date)) return "sutra";
  return format(date, "d. MMMM", { locale: srLatn });
}

function formatSuggestedDay(
  slot: Date,
  isClient: boolean,
  includeTime: boolean,
) {
  const displayDate = isClient
    ? toZonedTime(slot, Intl.DateTimeFormat().resolvedOptions().timeZone)
    : toZonedTime(slot, "Europe/Belgrade");

  const dayLabel = isToday(displayDate)
    ? "Danas"
    : isTomorrow(displayDate)
      ? "Sutra"
      : format(displayDate, "EEEE, d. MMMM", { locale: srLatn });

  if (!includeTime) return dayLabel;

  const time = format(displayDate, "HH:mm", { locale: srLatn });
  return `${dayLabel} u ${time}`;
}

function TherapistNextAvailableDays({
  selectedDate,
  suggestedDays,
  onSelectDay,
  isClient,
}: {
  selectedDate: Date;
  suggestedDays: SuggestedAvailableDay[];
  onSelectDay: (day: SuggestedAvailableDay) => void;
  isClient: boolean;
}) {
  return (
    <div className="py-2 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-background text-muted-foreground shadow-xs ring-1 ring-border">
        <CalendarSearch className="h-5 w-5" />
      </div>
      <p className="font-medium text-foreground">
        Nema slobodnih termina {formatSelectedDateRelative(selectedDate)}
      </p>
      {suggestedDays.length > 0 ? (
        <>
          <p className="mt-1 text-sm text-muted-foreground">
            Prvi slobodni dani su:
          </p>
          <div className="-mx-4 mt-4 grid gap-2 md:mx-auto md:max-w-sm">
            {suggestedDays.map((day, index) => (
              <Button
                key={`${day.availabilityKey}-${day.displayDayKey}`}
                variant={index === 0 ? "cta" : "outline"}
                className="w-full justify-center"
                onClick={() => onSelectDay(day)}
              >
                {formatSuggestedDay(day.displaySlot, isClient, index === 0)}
              </Button>
            ))}
          </div>
        </>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground">
          Trenutno nema slobodnih termina kod ovog terapeuta.
        </p>
      )}
    </div>
  );
}

export default function TherapistsPublicTimeslots({
  therapistUserId,
  overridePathname,
  therapist,
  sessionPurpose = TherapySessionPurpose.THERAPY,
  onDateTimeSelect,
  selectedDateTime,
  asClientView,
}: {
  therapistUserId: string;
  overridePathname?: string;
  therapist?: NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>;
  sessionPurpose?: TherapySessionPurpose;
  onDateTimeSelect?: (dateTime: Date) => void;
  selectedDateTime?: Date | null;
  asClientView?: boolean;
}) {
  const [{ date: dateQuery }, setBookingParams] = useQueryStates({
    date: parseAsIsoDateTime,
    step: parseAsInteger,
  });
  const [initialDateQuery] = useQueryState("initialDate", parseAsString);
  const [therapyTypeQuery] = useQueryState(
    "therapyType",
    parseAsStringEnum<TherapyRelationshipType>(
      Object.values(TherapyRelationshipType),
    ).withDefault(TherapyRelationshipType.INDIVIDUAL),
  );
  const stableToday = useMemo(() => startOfDay(new Date()), []);
  const stableMaxDate = useMemo(() => addDays(stableToday, 30), [stableToday]);
  const initialDate = useMemo(
    () => parseInitialDate(initialDateQuery),
    [initialDateQuery],
  );

  const [date, setDate] = useState<Date>(
    () => dateQuery ?? initialDate ?? stableToday,
  );
  const { addToBookingCart, isPending } = useBookingCart();

  const [timezone, setTimezone] = useState("UTC");
  const [isTimezoneResolved, setIsTimezoneResolved] = useState(false);

  useEffect(() => {
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    setIsTimezoneResolved(true);
  }, []);

  const availableTimeslots = api.calendar.getAvailableTimeSlots.useQuery(
    {
      therapistUserId,
      timezone,
      asClientView,
      sessionType: therapyTypeQuery,
      sessionPurpose,
    },
    { enabled: isTimezoneResolved, staleTime: 10 * 60 * 1000 },
  );

  const pathname = usePathname();

  if (availableTimeslots.isLoading) {
    return (
      <CustomCalendar
        mode="single"
        selected={selectedDateTime || date}
        fromDate={stableToday}
        toDate={stableMaxDate}
        availableTimeslots={{}}
        markedDates={[stableToday]}
        noTimeslotsContent={() => null}
        data-testid="time-slot"
      />
    );
  }

  if (!availableTimeslots.data) {
    return null;
  }

  const handleDateSelect = async (selectedDate: Date | undefined) => {
    if (!selectedDate || isPending) return;

    setDate(selectedDate);

    if (selectedDate.getHours() !== 0 || selectedDate.getMinutes() !== 0) {
      const timestamp = setMilliseconds(setSeconds(selectedDate, 0), 0);

      if (therapist) {
        const currentUrl = overridePathname || pathname;
        const therapyType = therapyTypeQuery;

        try {
          if (sessionPurpose !== TherapySessionPurpose.INTRO_CALL)
            await addToBookingCart({
              therapistUserId: therapist.userId,
              therapistSlug: therapist.slug,
              selectedDateTime: timestamp.toISOString(),
              therapyType,
              bookingUrl: currentUrl,
              therapistName: therapist.user.name || undefined,
              therapistImage: therapist.user.image || undefined,
              currentStep: 1,
              timezone,
              minimumBookingLeadTime: therapist.minimumBookingLeadTime,
            });

          await setBookingParams({ date: timestamp, step: 1 });

          if (onDateTimeSelect) {
            onDateTimeSelect(timestamp);
          }
        } catch (error) {
          console.error("Failed to store booking intent:", error);
        }
      }
    }
  };

  const today = stableToday;
  const maxDate = stableMaxDate;
  const markedDate = stableToday;

  return (
    <div className="w-full">
      <CustomCalendar
        mode="single"
        onSelect={handleDateSelect}
        selected={selectedDateTime || date}
        fromDate={today}
        toDate={maxDate}
        availableTimeslots={availableTimeslots.data.freeTimeSlots}
        markedDates={[markedDate]}
        noTimeslotsContent={(selectedDate) => (
          <TherapistNextAvailableDays
            selectedDate={selectedDate}
            suggestedDays={getNextAvailableDays({
              selectedDate,
              freeTimeSlots: availableTimeslots.data.freeTimeSlots,
              timezone,
            })}
            onSelectDay={(day) => {
              setDate(day.availabilityDate);
            }}
            isClient={isTimezoneResolved}
          />
        )}
        timeslotsFooterContent={(selectedDate) => (
          <OtherTherapistsTimeDrawer
            initialDate={selectedDate}
            sessionType={therapyTypeQuery}
            timezone={timezone}
            isTimezoneResolved={isTimezoneResolved}
          />
        )}
        data-testid="time-slot"
      />
    </div>
  );
}
